import qs from 'qs'
import moment from 'moment'
import { registerInterceptors } from '@/functions/index.js'

import { createWebGateway, useWebGateway, useVersionCheck, useLogger, createNethoneDftp } from '@booksy-com/vue-tools'

import { EVENTS, FEATURE_FLAGS } from '@/lib/consts.js'
import { loadIosTabletIdentity } from '@/lib/ios-tablet-identity.js'
import { getWebGatewayUrl } from '@/lib/get-web-gateway-url.js'

import { useCore } from '@/composables/core'
import { useAuthentication, LOGOUT_REASONS } from '@/composables/authentication/index.js'
import { createNativeIdentity } from '@/composables/native-identity/index.js'
import { createAnalytics, useAnalytics } from '@/composables/analytics/index.js'
import { createDeeplinksService } from '@/composables/deeplinks/index.js'
import { useEmitter } from '@/composables/emitter'
import { useWebViewEventBus } from '@/composables/web-view-event-bus/index.js'
import { usePushNotifications } from '@/composables/push-notifications/index.js'
import { useDefaultRedirect } from '@/composables/default-redirect'
import { useRedirection } from '@/composables/default-redirect/redirection'
import { useToast } from '@/composables/toast'
import { useReady } from '@/composables/ready'
import { useMaintenanceMode } from '@/composables/maintenance-mode/index.js'
import { useEppo } from './composables/eppo/index.js'
import { useLoyaltyAnalytics } from '@/composables/loyalty-analytics/index.js'
import { openSupportWhenPossible } from '@/composables/chat/index.js'

let $root = null

const createServices = () => {
  const logger = useLogger('bootstrap')

  logger.debug('bootstrapping core services')

  logger.debug('setting up NativeIdentityService...')
  createNativeIdentity()

  logger.debug('setting up PushNotificationsService (composable)...')
  const pushNotifications = usePushNotifications()
  pushNotifications.init()

  logger.debug('setting up DeeplinksService (composable)...')
  createDeeplinksService()

  logger.debug('setting up WebViewEventBus (composable)...')
  const webViewEventBus = useWebViewEventBus()
  webViewEventBus.init()

  logger.debug('setting up Analytics (composable)...')
  createAnalytics()

  if (useEppo().ff(FEATURE_FLAGS.Feature_NethoneEnabled)) {
    logger.debug('Setting up nethone (composable)...')
    createNethoneDftp()
  }
}

const createWebGatewayConnection = async () => {
  const logger = useLogger('bootstrap')

  logger.debug('bootstrapping web-gateway connection')

  const { store, t } = useCore()

  if (window.__E2E__?.disableWebGateway === true) {
    logger.debug('disable web-gateway for E2E test')
    store.commit('identity/setWebGatewayDisabled')
    return Promise.resolve()
  }

  const toasted = useToast()

  const env = process.env
  const webGatewayUrl = getWebGatewayUrl(env)
  const { ready } = createWebGateway(webGatewayUrl, store.state.app.country)

  try {
    await ready
  } catch (e) {
    logger.error(e)
    toasted.error(t('connection-problem'))
    store.commit('identity/setWebGatewayDisabled')
    return Promise.resolve()
  }
}

const bootstrapCoreServices = async () => {
  /*
  * services
  */
  createServices()

  /*
  * interceptors
  */
  registerInterceptors()

  /*
  * web-gateway connection
  */
  await createWebGatewayConnection()
}

const setFirstDayOfWeek = (data) => {
  const { first_day_of_week } = data
  moment.updateLocale(moment.locale(), {
    week: {
      dow: first_day_of_week,
    },
  })
}

export async function bootstrap ({ context }) {
  $root = context
  const { store, router } = useCore()
  const logger = useLogger('bootstrap')

  const defaultRedirect = useDefaultRedirect()
  const webViewEventBus = useWebViewEventBus()
  const emitter = useEmitter()

  // BEGIN `CREATED` HOOK FROM MAIN.JS
  const { setReady, whenReady } = useReady()

  whenReady.then(() => {
    logger.info('ready')

    if (store.state.app.webView) {
      webViewEventBus.events.ehlo()
    }

    const analytics = useAnalytics()
    analytics.dispatch(analytics.EVENTS.INIT)
  })

  // !IMPORTANT!
  // store, services, interceptors, ... MUST be initialized BEFORE
  // we perform any business logic
  try {
    await bootstrapCoreServices()
  } catch (e) {
    logger.error('fatal error; failed to initialize core services; ', e)
  }

  const {
    startCheckingMaintenanceMode,
    isMaintenanceMode,
  } = useMaintenanceMode()

  await startCheckingMaintenanceMode()
  if (isMaintenanceMode.value) {
    logger.info('maintenance mode enabled... skip app bootstrap')
    return
  }

  // transform webBiz path to frontdesk path if necessary
  const queryParams = qs.parse(window.location.search.substring(1))
  let webBizRedirect = null
  if (queryParams.r) {
    webBizRedirect = await import('@/lib/web-biz-redirect.js')
      .then(({ default: webBizRedirect }) => webBizRedirect(queryParams))
  }

  const redirection = {
    path: window.location.pathname.split(router.options.history.base)[1],
    query: webBizRedirect?.query || queryParams,
    params: webBizRedirect?.params || {},
  }

  // we need chnage onboarding path to name, cause of substeps
  if (redirection.path.includes('onboarding')) {
    redirection.name = 'onboarding'
  }

  if (window.location.pathname.includes('logout')) {
    redirection.name = 'onboarding'
    if (Number(redirection.query.clear) === 1) {
      localStorage.clear()
      delete redirection.query.clear
    }
  }

  if (redirection.query['redirect']) {
    redirection.name = redirection.query['redirect']

    // Handle loyalty program redirect analytics
    if (redirection.name === 'settings.loyalty-program') {
      const { updatePropertiesContext } = useLoyaltyAnalytics()

      updatePropertiesContext({
        clickSource: 'deeplink',
      })

      logger.debug('Loyalty program redirect detected, context built')
    }

    if (redirection.name === 'settings-online-booking') {
      redirection.query = redirection.query || {}
      redirection.query.entry_source = redirection.query.entry_source || 'deeplink'
    }

    delete redirection.query['redirect']
    if (redirection.query['business_id']) {
      redirection.params['businessId'] = parseInt(redirection.query['business_id'], 10)
      delete redirection.query['business_id']
    }
  }

  // store redirection for defaultRedirect()
  useRedirection().set(redirection)

  // Check if support chat should be opened
  if (redirection?.query?.support === 'true') {
    openSupportWhenPossible()
    delete redirection.query.support
  }

  try {
    logger.debug('loading routing config')
    await store.dispatch('routingConfig/load')
      .then(setFirstDayOfWeek)
  } catch (error) {
    return
  }

  let identity

  // keep in mind that `auth_token` has a short lifespan (120s afair)
  // and should be used only to obtaining an `access_token` for further authorization
  const authToken = redirection.query?.auth_token

  const accessToken = redirection.query?.accessToken
    || redirection.query?.access_token

  // ios tablet identity has to be persisted on frontdesk localStorage
  // for the native app to restore it properly on startup
  identity = loadIosTabletIdentity(store)

  if (authToken) {
    try {
      logger.debug('restoring identity with auth token')
      identity = await store.dispatch('api/loginToken', { authToken })
    } catch (e) {
      logger.debug('Cannot login with _auth_ token')
    } finally {
      delete redirection.query.auth_token
    }
  } else if (accessToken) {
    logger.debug('restoring identity with access token')
    try {
      identity = await store.dispatch('api/loginToken', { accessToken })
    } catch (e) {
      logger.debug('Cannot login with _access_ token')
    } finally {
      delete redirection.query.accessToken
      delete redirection.query.access_token
    }
  } else if (!identity && store.state.identity.isWebGatewayEnabled) {
    const { getIdentity, clearIdentity } = useWebGateway()

    const isWebViewOnboardingWithEmail = store.state.app.webView
      && redirection?.path?.includes('onboarding')
      && redirection?.query?.email

    if (isWebViewOnboardingWithEmail) {
      logger.debug('clearing identity in web-gateway')
      await clearIdentity()
    } else {
      logger.debug('restoring identity from web-gateway')
      const identityFromWebGateway = await getIdentity()
      identity = identityFromWebGateway ? {
        accessToken: identityFromWebGateway.accessToken,
        user: identityFromWebGateway.profile,
        loginType: identityFromWebGateway.loginType,
      } : null
    }
  }

  const mobileRoute = redirection?.path.includes('subscription-mobile')
  if (mobileRoute && authToken && !identity) {
    router.replace({ name: 'subscription-mobile-error' })
  }

  if (identity && identity.accessToken) {
    // do not redirect internally
    try {
      await useAuthentication().login.bind($root)(identity, false)
    } catch (e) {
      logger.error(e)
    }
  }

  if (window.location.pathname.includes('logout')) {
    await useAuthentication().logout(false, {
      reason: LOGOUT_REASONS.DEFAULT_LOGOUT,
    })
  }

  // redirect
  if (!mobileRoute) {
    const r = defaultRedirect()
    if (r === false) return
  } else {
    if (!store.state.business.business) {
      router.replace({ name: 'subscription-mobile-error' })
    }
  }

  const { checkVersionChange } = useVersionCheck()
  emitter.on(EVENTS.CHECK_VERSION_CHANGE, () => checkVersionChange(store.state.app.basePath))

  if (store.state.app.webView) {
    emitter.on(EVENTS.WEB_VIEW_RESUME, () => { emitter.emit(EVENTS.CHECK_VERSION_CHANGE) })
    emitter.on(EVENTS.WEB_VIEW_CLEAR_CACHE, () => { webViewEventBus.events.clearWebViewCache() })
  }

  // done!
  setReady()
  // END `CREATED` HOOK FROM MAIN.JS
}
